import { useMemo } from 'react';
import type { OralExamQuestion } from '@/common/api/models/oralExamQuestion';
import { splitFillAnchor, type OralExamFillKind } from '@/common/components/oralExamPreviewQuestion';
import { useSelectedModule } from '@/common/hooks/student/useSelectedModule';
import { isOralExamPracticeTask } from '@/common/utils/oralExam/practiceApi';

export type LinkedOralExamQuestion = Omit<OralExamQuestion, 'fillKind'> & {
  fillKind?: OralExamFillKind | string | null;
};

/**
 * - ``linked``: the graded oral exam's pool has questions whose source is this task.
 * - ``unlinked``: the module has a graded oral exam, but no question names this task
 *   (or the booked exam version could not load).
 * - ``none``: the module has no graded oral exam.
 */
export type LinkedOralExamStatus = 'linked' | 'unlinked' | 'none';

export interface LinkedOralExamQuestions {
  status: LinkedOralExamStatus;
  questions: LinkedOralExamQuestion[];
  /** True when any linked question shows the "…" fill mark. */
  hasFill: boolean;
}

type ModuleTaskLike = {
  type?: string;
  eventConfig?: { eventKindId?: string } | null;
  oralExamConfig?: {
    questionPool?: LinkedOralExamQuestion[] | null;
    questionPoolUnavailable?: boolean | null;
  } | null;
};

export function questionHasFill(question: LinkedOralExamQuestion): boolean {
  return splitFillAnchor(question.question ?? '', question.fillKind, { allowUniversalCue: true }) !== null;
}

/** Pool questions that name ``taskId`` as their source, in pool order. Practice exams never count. */
export function selectLinkedOralExamQuestions(
  taskId: string,
  moduleTasks: ModuleTaskLike[],
): LinkedOralExamQuestions {
  const oralExam = moduleTasks.find(
    (task) => task.type === 'oral_exam' && !isOralExamPracticeTask(task),
  );
  if (!oralExam) return { status: 'none', questions: [], hasFill: false };

  const config = oralExam.oralExamConfig;
  if (!config || config.questionPoolUnavailable) {
    return { status: 'unlinked', questions: [], hasFill: false };
  }

  const questions = (config.questionPool ?? []).filter(
    (question) => (question.referringTaskIds ?? []).includes(taskId),
  );
  if (questions.length === 0) return { status: 'unlinked', questions: [], hasFill: false };

  return { status: 'linked', questions, hasFill: questions.some(questionHasFill) };
}

export function useLinkedOralExamQuestions(taskId: string): LinkedOralExamQuestions {
  const { tasks } = useSelectedModule();
  return useMemo(
    () => selectLinkedOralExamQuestions(taskId, (tasks ?? []) as ModuleTaskLike[]),
    [taskId, tasks],
  );
}
